/**
 * Pauses written inside a line of narration.
 *
 * There are two kinds of silence in a script and they are not the same thing. A
 * pause *between* lines is a gap the timeline leaves — `pauseBefore` — and it
 * costs nothing to make, because no words changed and every voice clip is
 * reused. A pause *inside* a line has to be spoken: it is a beat between two
 * halves of one sentence, part of the delivery, so it belongs in the text and
 * the line is said again when it changes.
 *
 * A marker in the text rather than a character offset stored beside it. An
 * offset is correct for exactly as long as nobody edits the words before it,
 * and the script is a document people type into — a marker moves with the
 * sentence it belongs to, survives a paste, and can be typed by hand.
 */

/** `[1.5s]`, `[2s]` — deliberately something a person can read and retype. */
const MARKER = /\[(\d+(?:\.\d+)?)s\]/g;

/**
 * The longest break worth asking for.
 *
 * Providers cap these, and a long one inside a sentence is a gap the listener
 * reads as a fault rather than a beat. Anything longer belongs between lines,
 * where it costs nothing.
 */
const MAX_SECONDS = 3;
const MIN_SECONDS = 0.1;

const clamp = (seconds: number) => Math.min(MAX_SECONDS, Math.max(MIN_SECONDS, seconds));

/** The marker to write into a script for a pause of this length. */
export function inlinePauseMarker(seconds: number): string {
  const value = clamp(Number.isFinite(seconds) ? seconds : MIN_SECONDS);
  return `[${Number(value.toFixed(2))}s]`;
}

/**
 * The line without its markers: what a caption shows and what a word count
 * counts, because nobody reads a marker aloud.
 *
 * The surrounding spaces collapse with it, so a marker written tight against
 * the words does not leave a seam where it was.
 */
export function spokenText(script: string): string {
  return script
    .replace(new RegExp(`\\s*${MARKER.source}\\s*`, "g"), " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * The line as the voice provider should receive it.
 *
 * Any angle-bracketed span is removed from the words first — removed whole, not
 * merely defanged. The text reaches a provider verbatim, so a script that
 * already contained markup would otherwise be instructions to the voice model
 * rather than something to say, and a script can come from a model or from
 * anyone with an editor. Stripping the brackets alone would leave the innards
 * behind as words, and the narrator would read them out.
 */
export function speechMarkup(script: string): string {
  const safe = script.replace(/<[^>]*>?/g, " ").replace(/>/g, " ");
  return safe
    .replace(MARKER, (_match, seconds: string) => {
      const value = clamp(Number(seconds));
      return `<break time="${Number(value.toFixed(2))}s" />`;
    })
    .replace(/\s+/g, " ")
    .trim();
}

/** Whether a line carries any inline pause at all. */
export function hasInlinePause(script: string): boolean {
  return new RegExp(MARKER.source).test(script);
}

/** How long a line's inline pauses hold, in seconds, each as the voice will hold it. */
export function inlinePauseSeconds(script: string): number {
  let total = 0;
  for (const match of script.matchAll(MARKER)) total += clamp(Number(match[1]));
  return Number(total.toFixed(2));
}
